/**
 * 2D centre visual for the processing console: a drifting particle field with
 * the seven validation layers (Domain, Requirement, Concept, Relationship,
 * Consistency, Knowledge Quality, Governance) orbiting the RegO AI core. Each
 * layer lights up as the run clears it, and the particle field takes the colour
 * of the outcome once there is one - red on failures, green on a clean result.
 *
 * Colours come from the app's theme tokens - Tailwind classes for the DOM,
 * `readPalette()` for the canvas - so the visual follows light/dark mode.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  VALIDATION_LAYERS,
  readPalette,
  type ConsoleTone,
  type PipelineRun,
} from "./processingPipeline";

const ORBIT_BOX = 340;
const ORBIT_RADIUS = 132;

/** Particle count and how far the field spreads past the orbit ring. */
const PARTICLE_COUNT = 120;
/** Kept clear of the core, the ring and its labels so dots never collide. */
const PARTICLE_CLEARANCE = ORBIT_RADIUS + 38;

/** Particle tints per outcome. Neutral follows the app's chart palette. */
const TONE_PARTICLES: Record<Exclude<ConsoleTone, "neutral">, string[]> = {
  fail: ["#ef4444", "#f87171", "#fca5a5", "#dc2626"],
  pass: ["#10b981", "#34d399", "#6ee7b7", "#059669"],
};

export function PipelineOrbit2D({
  run,
  paused = false,
  settled = false,
  tone = "neutral",
}: {
  run: PipelineRun;
  /** Console is hidden (minimised) - don't burn a frame loop behind it. */
  paused?: boolean;
  /**
   * The validation result has landed, so a layer that is not lit is genuinely
   * not cleared. While this is false the outcome is still unknown and layers
   * stay neutral rather than reading as failures.
   */
  settled?: boolean;
  /** Tints the particle field: red on failures, green on a clean result. */
  tone?: ConsoleTone;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(1);
  /* The particle field needs the current scale when it seeds, and seeding runs
     outside React's render, so the value is mirrored into a ref. */
  const scaleRef = useRef(1);

  // Keep the fixed-geometry orbit inside whatever space the panel gives it.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      const room = Math.min(width, height) - 24;
      const next = Math.max(0.45, Math.min(1, room / ORBIT_BOX));
      scaleRef.current = next;
      setScale(next);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Particle field, tinted with the app's chart palette. Re-seeds when the
  // console is restored, since a hidden wrapper measures zero.
  useEffect(() => {
    if (paused) return;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let raf = 0;
    let colors = readPalette().chart;
    let parts: {
      a: number;
      rad: number;
      r: number;
      sp: number;
      ph: number;
      color: string;
    }[] = [];

    const seed = () => {
      colors =
        tone === "neutral"
          ? readPalette().chart
          : (TONE_PARTICLES[tone] as unknown as typeof colors);
      const rect = wrap.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width));
      canvas.height = Math.max(1, Math.floor(rect.height));

      /* Particles ride circular paths, so a radius past the canvas corner is
         simply never drawn. Spread runs from just outside the orbit ring out
         to the corner, which fills the panel instead of ringing the core. */
      const maxRad = Math.hypot(canvas.width, canvas.height) / 2;
      const minRad = Math.min(
        PARTICLE_CLEARANCE * scaleRef.current,
        maxRad * 0.7,
      );
      const spread = Math.max(40, maxRad - minRad);

      parts = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        a: Math.random() * Math.PI * 2,
        rad: minRad + Math.random() * spread,
        r: 0.6 + Math.random() * 1.6,
        sp: 0.15 + Math.random() * 0.4,
        ph: Math.random() * Math.PI * 2,
        color: colors[i % colors.length],
      }));
    };

    seed();
    const ro = new ResizeObserver(seed);
    ro.observe(wrap);
    // Re-seed when the theme flips so particle colours follow.
    const themeObserver = new MutationObserver(seed);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const frame = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      for (const p of parts) {
        if (!reduced) p.a += p.sp * 0.004;
        const x = cx + Math.cos(p.a) * p.rad;
        const y = cy + Math.sin(p.a) * p.rad;
        const tw = 0.5 + 0.5 * Math.sin((t / 700) * p.sp * 4 + p.ph);
        ctx.beginPath();
        ctx.globalAlpha = 0.2 + 0.4 * tw;
        ctx.fillStyle = p.color;
        ctx.arc(x, y, p.r * (0.6 + 0.5 * tw), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      themeObserver.disconnect();
    };
  }, [paused, tone]);

  return (
    <div ref={wrapRef} className="relative grid h-full w-full place-items-center">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      <div
        className="relative"
        style={{
          width: ORBIT_BOX,
          height: ORBIT_BOX,
          transform: `scale(${scale})`,
        }}
      >
        {/* Dashed orbit guide, counter-rotating behind the nodes */}
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-border/70"
          style={{
            width: ORBIT_RADIUS * 2,
            height: ORBIT_RADIUS * 2,
            animation: "rgc-spin-rev 48s linear infinite",
          }}
        />

        {/* Spokes + validation-layer nodes */}
        {VALIDATION_LAYERS.map((s, i) => {
          const angle =
            (-90 + i * (360 / VALIDATION_LAYERS.length)) * (Math.PI / 180);
          const lit = i < run.layersLit;
          /* Only mark a layer as being checked once the backend has actually
             cleared some - with no per-layer progress there is nothing to point
             at, and parking the indicator on the first layer reads as stuck. */
          const isActive = !run.done && run.layersLit > 0 && i === run.layersLit;
          const Icon = s.icon;
          return (
            <div key={s.key}>
              <span
                className="absolute left-1/2 top-1/2 z-[1] h-px origin-left transition-opacity duration-500"
                style={{
                  width: ORBIT_RADIUS,
                  transform: `rotate(${angle}rad)`,
                  opacity: lit ? 0.6 : 0.15,
                  background: lit
                    ? `linear-gradient(90deg, ${s.accent}, transparent)`
                    : "var(--border)",
                }}
              >
                {/* Packet travelling out to the layer being cleared */}
                {isActive && (
                  <span
                    className="absolute left-0 top-1/2 size-1.5 rounded-full"
                    style={
                      {
                        background: s.accent,
                        boxShadow: `0 0 8px ${s.accent}`,
                        "--rgc-reach": `${ORBIT_RADIUS - 20}px`,
                        animation: "rgc-travel 1.6s ease-in-out infinite",
                      } as React.CSSProperties
                    }
                  />
                )}
              </span>
              <div
                className="absolute z-[2] w-24 -translate-x-1/2 -translate-y-1/2 text-center"
                style={{
                  left: ORBIT_BOX / 2 + Math.cos(angle) * ORBIT_RADIUS,
                  top: ORBIT_BOX / 2 + Math.sin(angle) * ORBIT_RADIUS,
                  // Staggered bob so the ring reads as alive, not a static diagram.
                  animation: "rgc-drift 4.5s ease-in-out infinite",
                  animationDelay: `${i * 0.55}s`,
                }}
              >
                {/* Cleared reads green and the layer being checked indigo. Red
                    is reserved for a layer the finished result did not clear -
                    while the job is still processing the outcome is unknown, so
                    the node stays neutral. */}
                <span
                  className={`relative mx-auto grid size-10 place-items-center rounded-xl border transition-all duration-500 ${
                    isActive
                      ? "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-300"
                      : lit
                        ? "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300"
                        : settled
                          ? "border-red-200 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-400"
                          : "border-border bg-card text-[#94a3b8] dark:text-white/30"
                  }`}
                  style={{
                    transform: isActive ? "scale(1.12)" : "none",
                  }}
                >
                  <Icon className="size-[18px]" />
                  {isActive && (
                    <span
                      className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-indigo-400/70"
                      style={{ animation: "rgc-ring 1.5s ease-out infinite" }}
                    />
                  )}
                </span>
                <span
                  className={`mt-1.5 block whitespace-nowrap text-[10px] transition-colors duration-500 ${
                    lit ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </div>
          );
        })}

        {/* Core: shockwaves, a rotating conic halo and a breathing sphere. */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-[3] size-24 -translate-x-1/2 -translate-y-1/2">
          {[0, 1, 2].map((n) => (
            <span
              key={n}
              className="absolute inset-0 rounded-full border border-indigo-400/45"
              style={{
                animation: "rgc-pulse-out 3.6s ease-out infinite",
                animationDelay: `${n * 1.2}s`,
              }}
            />
          ))}
        </div>

        <span
          className="pointer-events-none absolute left-1/2 top-1/2 z-[3] size-32 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-md"
          style={{
            background:
              "conic-gradient(from 0deg, transparent, var(--chart-1), transparent 45%, var(--chart-2), transparent)",
            animation: "rgc-spin 9s linear infinite",
          }}
        />

        {/* Core mark: the RegO shield, breathing in place of the old sphere.
            The shockwaves and conic halo still radiate off it, so the visual
            energy is unchanged - only the brand mark at the centre swaps in. */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-[4] grid size-24 -translate-x-1/2 -translate-y-1/2 place-items-center"
          style={{ animation: "rgc-breathe 3.4s ease-in-out infinite" }}
        >
          <RegoShieldMark size={80} />
        </div>
      </div>
    </div>
  );
}

/**
 * The RegO shield mark, matching the one on the Login page. Renders two
 * gradient palettes and toggles them via Tailwind's `dark:` variant so the
 * mark stays legible against the console's background in both themes:
 * the deep indigo/magenta palette reads well on the light-mode paper card,
 * and the brighter indigo/violet/fuchsia palette pops against the near-black
 * dark-mode surface where the deeper colours would otherwise recede.
 *
 * Gradient ids are suffixed per variant so both SVGs can live in the DOM
 * simultaneously (only one is visible at a time) without id clashes.
 */
function RegoShieldMark({ size = 48 }: { size?: number }) {
  return (
    <>
      <div className="dark:hidden">
        <RegoShieldSvg
          size={size}
          idSuffix="light"
          shadow="drop-shadow(0 8px 18px rgba(99, 102, 241, 0.35))"
          palette={LIGHT_SHIELD_PALETTE}
        />
      </div>
      <div className="hidden dark:block">
        <RegoShieldSvg
          size={size}
          idSuffix="dark"
          shadow="drop-shadow(0 8px 22px rgba(168, 139, 250, 0.45))"
          palette={DARK_SHIELD_PALETTE}
        />
      </div>
    </>
  );
}

interface ShieldPalette {
  /** Linear back-left half of the shield. */
  g1: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Linear back-right half. */
  g2: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Radial left inner face. */
  g3: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Radial right inner face. */
  g4: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Radial left overlay. */
  g5: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Radial right overlay. */
  g6: readonly [
    { offset: string; color: string },
    { offset: string; color: string },
  ];
  /** Check-mark colour (kept white in both themes for contrast). */
  check: string;
  /** Shadow under the check-mark stroke. */
  checkShadow: string;
}

const LIGHT_SHIELD_PALETTE: ShieldPalette = {
  g1: [
    { offset: "0%", color: "#2B378C" },
    { offset: "51%", color: "#2E3285" },
    { offset: "86%", color: "#372C77" },
    { offset: "100%", color: "#3D2A70" },
  ],
  g2: [
    { offset: "7%", color: "#882783" },
    { offset: "50%", color: "#314196" },
    { offset: "93%", color: "#364F9F" },
  ],
  g3: [
    { offset: "0%", color: "#261E58" },
    { offset: "50%", color: "#455CA4" },
    { offset: "100%", color: "#261E58" },
  ],
  g4: [
    { offset: "0%", color: "#D684B7" },
    { offset: "100%", color: "#832573" },
  ],
  g5: [
    { offset: "0%", color: "#372D77" },
    { offset: "68%", color: "#455CA4" },
    { offset: "100%", color: "#455CA4" },
  ],
  g6: [
    { offset: "0%", color: "#842573" },
    { offset: "100%", color: "#D684B7" },
  ],
  check: "white",
  checkShadow: "drop-shadow(1px 2px 2px rgba(0,0,0,0.47))",
};

const DARK_SHIELD_PALETTE: ShieldPalette = {
  g1: [
    { offset: "0%", color: "#818cf8" },
    { offset: "51%", color: "#8b7cf6" },
    { offset: "86%", color: "#a78bfa" },
    { offset: "100%", color: "#c084fc" },
  ],
  g2: [
    { offset: "7%", color: "#e879f9" },
    { offset: "50%", color: "#a5b4fc" },
    { offset: "93%", color: "#818cf8" },
  ],
  g3: [
    { offset: "0%", color: "#6366f1" },
    { offset: "50%", color: "#a5b4fc" },
    { offset: "100%", color: "#4f46e5" },
  ],
  g4: [
    { offset: "0%", color: "#f0abfc" },
    { offset: "100%", color: "#d946ef" },
  ],
  g5: [
    { offset: "0%", color: "#8b5cf6" },
    { offset: "68%", color: "#a5b4fc" },
    { offset: "100%", color: "#a5b4fc" },
  ],
  g6: [
    { offset: "0%", color: "#d946ef" },
    { offset: "100%", color: "#f0abfc" },
  ],
  check: "white",
  checkShadow: "drop-shadow(1px 2px 3px rgba(0,0,0,0.6))",
};

function RegoShieldSvg({
  size,
  idSuffix,
  shadow,
  palette,
}: {
  size: number;
  idSuffix: string;
  shadow: string;
  palette: ShieldPalette;
}) {
  const id = (n: number) => `rgc-shield-${n}-${idSuffix}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      style={{ filter: shadow }}
    >
      <defs>
        <linearGradient id={id(1)} x1="0%" y1="100%" x2="100%" y2="0%">
          {palette.g1.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        <linearGradient id={id(2)} x1="100%" y1="0%" x2="0%" y2="100%">
          {palette.g2.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        <radialGradient id={id(3)} cx="37%" cy="43%" r="81%">
          {palette.g3.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(4)} cx="40%" cy="38%" r="76%">
          {palette.g4.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(5)} cx="18%" cy="56%" r="58%">
          {palette.g5.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <radialGradient id={id(6)} cx="74%" cy="67%" r="56%">
          {palette.g6.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
      </defs>
      <path
        d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
        fill={`url(#${id(1)})`}
      />
      <path
        d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
        fill={`url(#${id(2)})`}
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill={`url(#${id(3)})`}
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill={`url(#${id(4)})`}
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill={`url(#${id(5)})`}
        opacity="0.7"
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill={`url(#${id(6)})`}
        opacity="0.7"
      />
      <path
        d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
        fill={palette.check}
        style={{ filter: palette.checkShadow }}
      />
    </svg>
  );
}
